import { useCallback, useEffect, useMemo } from "react";

import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import { useGetCustomer } from "../../../../../common/hooks/useGetCustomer";
import { skuMap } from "../../../../../common/models/enums";
import StyledLoadingButton from "../../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { DEFAULT_DEVICE_PASSWORD } from "../../../../../common/variables/common";
import { errorNotification } from "../../../../../common/variables/notification";
import { useGetDevicesMakesCached } from "../../devices/hooks/useGetDevicesMakesCached";
import { useCreateSKU } from "../hooks/useCreateSKU";
import {
  ISKUForm,
  defaultSKUForm,
  modelLabelMap,
  skuCategoryOptions,
} from "../variables/skuData";
import SKUFormItem from "./SKUFormItem";

const CreateSKU = (): JSX.Element => {
  const { createSKU, loading } = useCreateSKU();
  const { data, loading: deviceMakesLoading } = useGetDevicesMakesCached();
  const { customer, loading: customerLoading } = useGetCustomer();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ISKUForm>({
    defaultValues: defaultSKUForm,
    mode: "onSubmit",
  });

  useEffect(() => {
    setValue("password", customer?.devicePassword || DEFAULT_DEVICE_PASSWORD);
  }, [customer?.devicePassword]);

  const watchedSKUData = watch("skuDataConfigurations");

  // Get all device types from DynamoDB (no hardcoded filter)
  const deviceTypeOptions = useMemo(() => {
    return (data?.getDevicesMakes?.items ?? [])
      .map(d => d?.model)
      .filter((model): model is string => Boolean(model))
      .sort();
  }, [data]);

  const getModelsToRunForDeviceType = useCallback(
    (deviceType: string): string[] => {
      const matched = data?.getDevicesMakes?.items?.find(
        d => d?.model === deviceType
      );

      // Filter to only include models supported by the backend
      const supportedModels = Object.keys(modelLabelMap);

      return (matched?.modelsToRun ?? []).filter(
        (model): model is string =>
          model !== null && supportedModels.includes(model)
      );
    },
    [data]
  );

  // Check if device type supports PT based on DeviceMake hasPanTilt field
  const getHasPanTiltForDeviceType = useCallback(
    (deviceType: string): boolean => {
      const matched = data?.getDevicesMakes?.items?.find(
        d => d?.model === deviceType
      );

      return matched?.hasPanTilt ?? false;
    },
    [data]
  );

  const addDevice = () => {
    const currentDevices = getValues("skuDataConfigurations");

    setValue("skuDataConfigurations", [
      ...currentDevices,
      {
        deviceType: "", // Force user to select - don't auto-select
        model: [],
      },
    ]);
  };

  const removeDevice = (indexToRemove: number) => {
    reset(prev => ({
      ...prev,
      skuDataConfigurations: prev.skuDataConfigurations.filter(
        (_d, index) => index !== indexToRemove
      ),
    }));
  };

  const onSubmit = async (data: ISKUForm) => {
    // Validate at least one device is added
    if (!data.skuDataConfigurations.length) {
      errorNotification("At least one device is required");

      return;
    }

    // Validate each device has required fields
    for (let i = 0; i < data.skuDataConfigurations.length; i++) {
      const device = data.skuDataConfigurations[i];

      if (!device.deviceType) {
        errorNotification(`Device ${i + 1}: Device Type is required`);

        return;
      }

      // Validate models - must have at least one non-empty model
      const validModels = (device.model || []).filter(
        m => m && m.trim() !== ""
      );

      if (validModels.length === 0) {
        errorNotification(`Device ${i + 1}: At least one model is required`);

        return;
      }

      // PT Type validation removed - PT Type is set at onboarding, not SKU creation
    }

    // Multiple PT device validation removed - trust OPS to create SKUs correctly

    // Validate top-level password length if provided
    if (data.password && data.password.length > 0 && data.password.length < 8) {
      errorNotification("Password must be at least 8 characters");

      return;
    }

    // Validate nodeCount
    if (!data.nodeCount || data.nodeCount < 1) {
      errorNotification("Node count must be at least 1");

      return;
    }

    const { password, hasRecordVideo, defaultIp, ...skuFormData } = data;

    await createSKU({
      ...skuFormData,
      nodeCount: Number(data.nodeCount),
      skuDataConfigurations: JSON.stringify(
        data.skuDataConfigurations.map(device => ({
          ...device,
          hasRecordVideo: hasRecordVideo,
          ...(password && { password }),
        }))
      ),
      ...(defaultIp?.trim() && { defaultIp: defaultIp.trim() }),
    });

    reset();
  };

  if (deviceMakesLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={2} sx={{ mb: 5 }} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={2}>
            <Controller
              name="skuNumber"
              control={control}
              rules={{
                required: "SKU Number is required",
              }}
              render={({ field }) => (
                <TextField
                  select
                  required
                  fullWidth
                  label="SKU Number"
                  {...field}
                  value={field.value ?? ""}
                  onChange={e => {
                    field.onChange(e.target.value);

                    setValue(
                      "configurationName",
                      skuMap[e.target.value as keyof typeof skuMap] ?? ""
                    );
                  }}
                  error={errors.skuNumber !== undefined}
                  helperText={errors.skuNumber?.message || "SKU/Item number"}
                >
                  {Object.keys(skuMap).map(key => (
                    <MenuItem key={key} value={key}>
                      {key}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name="configurationName"
              control={control}
              rules={{
                required: "Configuration is required",
              }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Configuration Name"
                  {...field}
                  value={field.value ?? ""}
                  helperText="Name for SKU Configuration"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Controller
              name="category"
              control={control}
              rules={{
                required: "Category is required",
              }}
              render={({ field }) => (
                <TextField
                  select
                  required
                  fullWidth
                  label="Category"
                  {...field}
                  value={field.value ?? ""}
                  error={errors.category !== undefined}
                  helperText={errors.category?.message || " "}
                >
                  <MenuItem value="">
                    <em>Select a category</em>
                  </MenuItem>
                  {skuCategoryOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Controller
              name="nodeCount"
              control={control}
              rules={{
                required: "Node count is required",
                min: { value: 1, message: "Must be at least 1" },
              }}
              render={({ field }) => (
                <TextField
                  required
                  fullWidth
                  type="number"
                  label="Node Count"
                  placeholder="Number of nodes"
                  {...field}
                  value={field.value ?? ""}
                  onChange={e => {
                    const value = e.target.value;

                    field.onChange(value === "" ? "" : parseInt(value, 10));
                  }}
                  error={errors.nodeCount !== undefined}
                  helperText={
                    errors.nodeCount?.message || "Number of nodes in this SKU"
                  }
                  inputProps={{ min: 1 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Controller
              name={"password"}
              control={control}
              defaultValue={""}
              rules={{
                validate: value => {
                  if (value && value.length > 0 && value.length < 8) {
                    return "Password must be at least 8 characters";
                  }

                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  label="Password (optional)"
                  {...field}
                  fullWidth
                  placeholder="Device password"
                  error={
                    !!errors?.password ||
                    !!(
                      field.value &&
                      field.value.length > 0 &&
                      field.value.length < 8
                    )
                  }
                  helperText={
                    errors?.password?.message ||
                    (field.value &&
                    field.value.length > 0 &&
                    field.value.length < 8
                      ? "Must be at least 8 characters"
                      : " ")
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Controller
              name="defaultIp"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Default IP (optional)"
                  placeholder="192.168.1.x"
                  {...field}
                  value={field.value ?? ""}
                  helperText="Pre-fills device IPs at onboarding"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md="auto">
            <Controller
              name="hasRecordVideo"
              control={control}
              render={({ field }) => (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ pt: 1 }}
                >
                  <Typography variant="body2">Enable Recording</Typography>
                  <Switch
                    checked={field.value ?? true}
                    onChange={e => field.onChange(e.target.checked)}
                  />
                </Stack>
              )}
            />
          </Grid>
        </Grid>

        <Stack spacing={2} marginBottom={3}>
          {watchedSKUData.map((device, index) => (
            <SKUFormItem
              key={device.deviceType + index}
              index={index}
              device={device}
              deviceTypeOptions={deviceTypeOptions}
              getModelsToRunForDeviceType={getModelsToRunForDeviceType}
              getHasPanTiltForDeviceType={getHasPanTiltForDeviceType}
              control={control}
              errors={errors}
              onRemove={removeDevice}
              watchedDeviceType={device.deviceType}
              setValue={setValue}
              watch={watch}
            />
          ))}
        </Stack>

        <Box mb={3}>
          {errors.skuDataConfigurations && (
            <FormHelperText error>
              <Typography variant="helperText" color="error">
                {errors.skuDataConfigurations.message}
              </Typography>
            </FormHelperText>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={customerLoading}
            onClick={e => {
              e.stopPropagation();

              addDevice();
            }}
          >
            Add Device
          </Button>
          <StyledLoadingButton
            disabled={loading}
            variant="contained"
            type="submit"
          >
            Create SKU
          </StyledLoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default CreateSKU;
